// ChecklistAccordionPanel.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ChecklistAccordionPanel.css';

// Components
import ChecklistInlineItem from './ChecklistInlineItem';
import Button from 'components/button/Button';

/**
 * ChecklistAccordionPanel
 *
 * Renders the expanded inline editor for a single checklist.
 * In readOnly: items stay visible; hide add / reorder / remove.
 *
 * @param {object} editor - Inline editor state and handlers from useChecklistInlineEditor.
 * @param {boolean} [readOnly=false] - Display-only.
 * @param {boolean} [hideItemStatus=false] - Hide checkboxes when per-item status is unknown (summary fallback).
 *
 * @returns {ReactNode} The rendered checklist accordion panel.
 */
const ChecklistAccordionPanel = ({
  editor,
  readOnly = false,
  hideItemStatus = false,
  allowCustomItems = true,
}) => {
  const { theme } = useTheme();

  const {
    items,
    handleToggleItem,
    handleItemTextChange,
    handleItemTextBlur,
    handleAddItem,
    handleRemoveItem,
    handleMoveItem,
  } = editor;

  const [draggedItemKey, setDraggedItemKey] = useState('');
  const [dragOverItemKey, setDragOverItemKey] = useState('');

  const isReorderEnabled = !readOnly && items.length > 1;

  const handleItemDragStart = (itemKey, event) => {
    if (readOnly || !itemKey || !isReorderEnabled) return;

    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', itemKey);
    setDraggedItemKey(itemKey);
    setDragOverItemKey(itemKey);
  };

  const handleItemDragOver = (itemKey, event) => {
    if (readOnly || !draggedItemKey || draggedItemKey === itemKey) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';

    if (dragOverItemKey !== itemKey) {
      setDragOverItemKey(itemKey);
    }
  };

  const handleItemDrop = (itemKey, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (readOnly || !draggedItemKey || draggedItemKey === itemKey) {
      setDragOverItemKey('');
      return;
    }

    handleMoveItem(draggedItemKey, itemKey);
    setDraggedItemKey('');
    setDragOverItemKey('');
  };

  const handleItemDragEnd = (event) => {
    event?.stopPropagation?.();
    setDraggedItemKey('');
    setDragOverItemKey('');
  };

  return (
    <div className={`checklist-accordion-panel${readOnly ? ' is-readonly' : ''}`} data-no-field-drag="true">
      <div className="checklist-accordion-panel-items">
        {items.length > 0 ? items.map((item) => {
          const isTemplateItem = String(item?.source || '').toLowerCase() === 'template'
            || Boolean(String(item?.templateKey || '').trim());

          return (
            <ChecklistInlineItem
              key={item.key}
              canRemove={!isTemplateItem}
              hideStatus={hideItemStatus}
              isDragOver={!readOnly && dragOverItemKey === item.key && draggedItemKey !== item.key}
              isDragging={!readOnly && draggedItemKey === item.key}
              isReorderEnabled={isReorderEnabled && !isTemplateItem}
              item={item}
              lockLabel={isTemplateItem}
              readOnly={readOnly}
              onDragEnd={handleItemDragEnd}
              onDragOver={handleItemDragOver}
              onDragStart={handleItemDragStart}
              onDrop={handleItemDrop}
              onItemTextBlur={handleItemTextBlur}
              onItemTextChange={handleItemTextChange}
              onRemoveItem={handleRemoveItem}
              onToggleItem={handleToggleItem}
            />
          );
        }) : (
          <div className="checklist-accordion-panel-empty">
            No items yet.
          </div>
        )}
      </div>

      {!readOnly && allowCustomItems ? (
        <div className="checklist-accordion-panel-footer">
          <Button
            gap={11}
            label="List Item"
            iconKey="add"
            iconSize={20}
            fontSize={13}
            foregroundColor={theme.foregroundMuted}
            hoverForegroundColor={theme.foreground}
            backgroundColor="transparent"
            padding="0px"
            width="auto"
            onClick={handleAddItem}
          />
        </div>
      ) : null}
    </div>
  );
};

ChecklistAccordionPanel.displayName = 'ChecklistAccordionPanel';

export default ChecklistAccordionPanel;
